import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Save, AlertTriangle, Eye, X, QrCode, Loader2, Building2, Settings2, Check, Plus,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import CustomerStatusPreview, { readTileData } from "./CustomerStatusPreview";
import { resolveIcon, ICON_CHOICES } from "../lib/liveStatusIcons";
import { categoryLabel } from "./AddTileModal";
import { useAuth } from "../lib/auth";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDateShortTime } from "../lib/dateFormat";

/**
 * PropertyStatusEditor + CustomerPreviewModal + TilePickerModal
 * Feb 2026 · Struktur-Fix Live-Status
 *
 * Schema-getriebener Ersatz für den bisherigen 6-Zeilen-Editor. Rendert
 * ALLE Punkte aus der systemweiten Master-Liste (`schema` prop). Basis-Punkte
 * (locked: last_inspection/temperature/water/heating/alarm/internet) speichern
 * weiterhin in den erstklassigen Property-Feldern; Custom-Punkte in
 * `property.custom_status[key]`.
 *
 * Der „Kacheln festlegen"-Button neben „Kundenansicht prüfen" öffnet den
 * TilePickerModal, in dem der Admin genau 6 Kacheln pro Property wählt.
 */

const NAVY = BRAND_COLORS.navy;
const GOLD = BRAND_COLORS.gold;
const GOLD_DARK = BRAND_COLORS.goldDark;
const RED = STATUS_COLORS.red;
const GREEN = STATUS_COLORS.green;
const BLUE = STATUS_COLORS.blue;
const IVORY = BRAND_COLORS.ivory;

const LOCKED_KEYS = new Set([
  "last_inspection", "temperature", "water", "heating", "alarm", "internet",
]);

/** Liest den aktuellen Editor-Wert für ein Schema-Item aus der Property.
 *  Basis-Keys aus first-class Feldern, Custom-Keys aus `custom_status`.
 */
function initialFieldValues(schema, property) {
  const acc = {};
  for (const it of schema) {
    if (LOCKED_KEYS.has(it.key)) {
      acc[`${it.key}__value`] = property[it.key] != null ? String(property[it.key]) : "";
      acc[`${it.key}__note`] = property[`${it.key}_note`] || "";
      acc[`${it.key}__sev`] = property[`${it.key}_severity`] === "attention" ? "attention" : "ok";
    } else {
      const entry = (property.custom_status || {})[it.key] || {};
      acc[`${it.key}__value`] = entry.value != null ? String(entry.value) : "";
      acc[`${it.key}__note`] = entry.note || "";
      acc[`${it.key}__sev`] = entry.severity === "attention" ? "attention" : "ok";
    }
  }
  return acc;
}

export default function PropertyStatusEditor({ property, customerId, schema, onSaved, onSchemaRefresh }) {
  const { user, effectiveRole } = useAuth();
  const isAdmin = effectiveRole === "admin";

  const [values, setValues] = useState(() => initialFieldValues(schema, property));
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [tilePickerOpen, setTilePickerOpen] = useState(false);

  // Re-init wenn Schema oder Property wechselt (z. B. externes Refresh) —
  // bewusst nur bei id/status_updated_at, damit ungespeicherte Edits nicht
  // bei jedem Parent-Reload verloren gehen. Aktuelles `property` via Ref.
  const propertyRef = useRef(property);
  useEffect(() => { propertyRef.current = property; });
  useEffect(() => {
    setValues(initialFieldValues(schema, propertyRef.current));
  }, [schema, property.id, property.status_updated_at]);

  const dirty = useMemo(() => {
    for (const it of schema) {
      const k = it.key;
      let origValue, origNote, origSev;
      if (LOCKED_KEYS.has(k)) {
        origValue = property[k] != null ? String(property[k]) : "";
        origNote = property[`${k}_note`] || "";
        origSev = property[`${k}_severity`] === "attention" ? "attention" : "ok";
      } else {
        const entry = (property.custom_status || {})[k] || {};
        origValue = entry.value != null ? String(entry.value) : "";
        origNote = entry.note || "";
        origSev = entry.severity === "attention" ? "attention" : "ok";
      }
      if ((values[`${k}__value`] || "") !== origValue) return true;
      if ((values[`${k}__note`] || "") !== origNote) return true;
      if ((values[`${k}__sev`] || "ok") !== origSev) return true;
    }
    return false;
  }, [values, property, schema]);

  const set = (key, v) => setValues((s) => ({ ...s, [key]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const body = {};
      const custom_status = {};
      for (const it of schema) {
        const k = it.key;
        const raw = values[`${k}__value`];
        const note = values[`${k}__note`];
        const sev = values[`${k}__sev`] === "attention" ? "attention" : "ok";
        if (LOCKED_KEYS.has(k)) {
          if (it.input_type === "number") {
            const n = raw === "" ? null : Number(raw);
            if (raw !== "" && Number.isNaN(n)) {
              toast.error(`${it.label}: bitte Zahl eingeben.`);
              setBusy(false);
              return;
            }
            body[k] = n;
          } else {
            body[k] = raw || null;
          }
          body[`${k}_note`] = note || null;
          body[`${k}_severity`] = sev;
        } else {
          custom_status[k] = {
            value: raw || null,
            note: note || null,
            severity: sev,
            label: it.label, // für Push-Notifications als Anzeigename
          };
        }
      }
      if (Object.keys(custom_status).length > 0) {
        body.custom_status = custom_status;
      }

      const { data: fresh } = await api.patch(`/properties/${property.id}`, body);
      // Wieviele haben JETZT frisch auf attention geflippt?
      const flagged = schema.filter((it) => {
        const nowSev = values[`${it.key}__sev`] === "attention";
        let wasSev = false;
        if (LOCKED_KEYS.has(it.key)) {
          wasSev = property[`${it.key}_severity`] === "attention";
        } else {
          wasSev = ((property.custom_status || {})[it.key] || {}).severity === "attention";
        }
        return nowSev && !wasSev;
      });
      if (flagged.length > 0) {
        toast.success(
          `${property.name} · ${flagged.length} Punkt${flagged.length === 1 ? "" : "e"} auf Handlungsbedarf gesetzt — Kunde wurde informiert.`,
          { duration: 4500 },
        );
      } else {
        toast.success(`${property.name} · Status aktualisiert — Kunde sieht es sofort.`);
      }
      onSaved?.(fresh);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  // Staged Property = SAVED + Editor-Änderungen (für Preview-Modal).
  const stagedProperty = useMemo(() => {
    const s = { ...property, custom_status: { ...(property.custom_status || {}) } };
    for (const it of schema) {
      const k = it.key;
      const raw = values[`${k}__value`];
      const note = values[`${k}__note`] || null;
      const sev = values[`${k}__sev`] === "attention" ? "attention" : "ok";
      if (LOCKED_KEYS.has(k)) {
        s[k] = it.input_type === "number"
          ? (raw === "" ? null : Number(raw))
          : (raw || null);
        s[`${k}_note`] = note;
        s[`${k}_severity`] = sev;
      } else {
        s.custom_status[k] = {
          value: raw || null,
          note,
          severity: sev,
          label: it.label,
        };
      }
    }
    // Falls IRGENDEIN Sev-Feld auf attention → property.status escalates.
    const anyAttention = schema.some((it) => values[`${it.key}__sev`] === "attention");
    s.status = anyAttention ? "attention" : "ready";
    return s;
  }, [property, values, schema]);

  return (
    <div className="bg-white border border-navy/10 p-5 md:p-6" data-testid={`property-status-editor-${property.id}`}>
      <div className="flex items-start gap-3 mb-5 flex-wrap">
        <Building2 className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={1.5} style={{ color: GOLD }} />
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg text-navy leading-tight">{property.name}</div>
          {(property.address || property.city) && (
            <div className="text-[12.5px] text-navy-dark/60 mt-0.5">
              {[property.address, property.city].filter(Boolean).join(", ")}
            </div>
          )}
          {property.status_updated_at && (
            <div className="text-[11px] text-navy-dark/50 italic mt-1">
              Zuletzt aktualisiert:{" "}
              {deDateShortTime(property.status_updated_at)}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {schema.map((it) => {
          const Icon = resolveIcon(it.icon);
          const isAttention = values[`${it.key}__sev`] === "attention";
          const isDate = it.input_type === "date";
          const isNumber = it.input_type === "number";
          return (
            <div
              key={it.key}
              className="p-3 transition-colors"
              style={{
                background: isAttention ? withAlpha(STATUS_COLORS.red, 0.05) : withAlpha(BRAND_COLORS.ivory, 0.5),
                borderWidth: isAttention ? "1.5px" : "1px",
                borderStyle: "solid",
                borderColor: isAttention ? RED : withAlpha(BRAND_COLORS.navy, 0.10),
              }}
              data-testid={`prop-status-row-${property.id}-${it.key}`}
            >
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Icon className="w-4 h-4" strokeWidth={1.4} style={{ color: isAttention ? RED : GOLD }} />
                <label className="label-caps" style={isAttention ? { color: RED } : { color: NAVY }} htmlFor={`prop-${property.id}-${it.key}`}>
                  {it.label}
                </label>
                {it.category && it.category !== "sonstiges" && (
                  <span
                    className="text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 ml-auto"
                    style={{ background: withAlpha(BRAND_COLORS.navy, 0.06), color: withAlpha(BRAND_COLORS.navy, 0.55) }}
                    title={`Kategorie: ${categoryLabel(it.category)}`}
                    data-testid={`prop-status-category-${property.id}-${it.key}`}
                  >
                    {categoryLabel(it.category)}
                  </span>
                )}
              </div>
              <input
                id={`prop-${property.id}-${it.key}`}
                data-testid={`prop-status-input-${property.id}-${it.key}`}
                type={isDate ? "date" : isNumber ? "number" : "text"}
                inputMode={isNumber ? "decimal" : undefined}
                step={isNumber ? "0.1" : undefined}
                value={values[`${it.key}__value`] || ""}
                onChange={(e) => set(`${it.key}__value`, e.target.value)}
                placeholder={it.placeholder || ""}
                disabled={busy}
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy-dark"
              />
              <input
                data-testid={`prop-status-note-${property.id}-${it.key}`}
                type="text"
                value={values[`${it.key}__note`] || ""}
                onChange={(e) => set(`${it.key}__note`, e.target.value)}
                placeholder="Notiz (optional)"
                disabled={busy}
                className="w-full bg-white border border-navy/15 focus:border-gold focus:outline-none px-3 py-1.5 text-[12px] text-navy-dark mt-2 italic"
              />
              {it.hint && <div className="text-[10.5px] text-navy-dark/50 mt-1">{it.hint}</div>}

              {/* Severity toggle */}
              <div className="flex items-center gap-2 mt-3" role="group" aria-label={`${it.label} · Handlungsbedarf`}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => set(`${it.key}__sev`, "ok")}
                  data-testid={`prop-status-sev-ok-${property.id}-${it.key}`}
                  aria-pressed={!isAttention}
                  className="text-[11px] tracking-widest uppercase px-3 py-1.5 border transition-colors"
                  style={!isAttention
                    ? { background: GREEN, color: IVORY, borderColor: GREEN }
                    : { background: "#fff", color: NAVY, borderColor: withAlpha(BRAND_COLORS.navy, 0.20) }}
                >
                  In Ordnung
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => set(`${it.key}__sev`, "attention")}
                  data-testid={`prop-status-sev-attention-${property.id}-${it.key}`}
                  aria-pressed={isAttention}
                  className="text-[11px] tracking-widest uppercase px-3 py-1.5 border transition-colors inline-flex items-center gap-1.5"
                  style={isAttention
                    ? { background: RED, color: IVORY, borderColor: RED }
                    : { background: "#fff", color: RED, borderColor: withAlpha(STATUS_COLORS.red, 0.35) }}
                >
                  <AlertTriangle className="w-3 h-3" strokeWidth={2} />
                  Handlungsbedarf
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex items-center justify-end gap-3 flex-wrap">
        {dirty && (
          <span className="text-[11px] tracking-wider uppercase" style={{ color: GOLD }}>
            · Ungespeicherte Änderungen
          </span>
        )}
        <button
          type="button"
          onClick={() => setTilePickerOpen(true)}
          disabled={!isAdmin}
          data-testid={`prop-status-tile-picker-${property.id}`}
          className="inline-flex items-center gap-2 text-navy px-4 py-2.5 text-[11px] tracking-[0.18em] uppercase border border-navy/25 hover:border-gold hover:text-gold-dark transition-colors bg-white disabled:opacity-40"
          title={isAdmin
            ? "Wähle genau 6 Punkte, die dem Kunden als Kacheln angezeigt werden."
            : "Nur Admins können die Kachel-Auswahl ändern."}
        >
          <Settings2 className="w-3.5 h-3.5" strokeWidth={1.6} />
          Kacheln für Kundenansicht festlegen
        </button>
        <button
          type="button"
          onClick={() => setPreviewOpen(true)}
          data-testid={`prop-status-preview-${property.id}`}
          className="inline-flex items-center gap-2 text-navy px-4 py-2.5 text-[11px] tracking-[0.18em] uppercase border border-navy/25 hover:border-gold hover:text-gold-dark transition-colors bg-white"
        >
          <Eye className="w-3.5 h-3.5" strokeWidth={1.6} />
          Kundenansicht prüfen
        </button>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || busy}
          data-testid={`prop-status-save-${property.id}`}
          className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-40"
          style={{ background: NAVY }}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" strokeWidth={1.6} />}
          Status speichern
        </button>
      </div>

      {previewOpen && (
        <CustomerPreviewModal
          saved={property}
          staged={stagedProperty}
          dirty={dirty}
          customerId={customerId}
          schema={schema}
          onClose={() => setPreviewOpen(false)}
        />
      )}
      {tilePickerOpen && (
        <TilePickerModal
          property={property}
          schema={schema}
          onClose={() => setTilePickerOpen(false)}
          onSaved={(fresh) => { setTilePickerOpen(false); onSaved?.(fresh); }}
          onSchemaRefresh={onSchemaRefresh}
        />
      )}
    </div>
  );
}

/** Overlay-Vorschau: rendert die 6 Kacheln des Kunden 1:1 (schema-getrieben).
 *  Umschalter „Gespeichert" ↔ „Mit ungespeicherten Änderungen".
 *  QR-Kunden-Ansicht bleibt intakt (2-min Impersonations-JWT). */
function CustomerPreviewModal({ saved, staged, dirty, customerId, schema, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const [mode, setMode] = useState("saved");
  const [qr, setQr] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState(null);
  const [remaining, setRemaining] = useState(0);

  const active = mode === "staged" && dirty ? staged : saved;
  const property = saved;

  const requestQr = async () => {
    if (!customerId) { setQrError("Kunden-ID fehlt — bitte Seite neu laden."); return; }
    setQrLoading(true);
    setQrError(null);
    try {
      const { data } = await api.post(`/team/customers/${customerId}/impersonation-link`);
      setQr(data);
      const secs = Math.max(0, Math.round(
        (new Date(data.expires_at).getTime() - Date.now()) / 1000
      ));
      setRemaining(secs);
    } catch (e) {
      setQrError(formatErr(e?.response?.data?.detail) || e.message || "QR-Code konnte nicht erzeugt werden.");
    } finally { setQrLoading(false); }
  };

  useEffect(() => {
    if (!qr) return;
    const timer = setInterval(() => {
      const secs = Math.max(0, Math.round(
        (new Date(qr.expires_at).getTime() - Date.now()) / 1000
      ));
      setRemaining(secs);
      if (secs <= 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [qr]);

  const updated = property.status_updated_at
    ? deDateShortTime(property.status_updated_at)
    : null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-0 md:p-6"
      style={{ background: withAlpha(BRAND_COLORS.navy, 0.55) }}
      onClick={onClose}
      data-testid={`customer-preview-modal-${property.id}`}
    >
      <div
        className="bg-ivory w-full md:max-w-[520px] shadow-2xl relative animate-fade-in"
        style={{ borderRadius: "12px 12px 0 0", maxHeight: "88vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-ivory/95 backdrop-blur px-5 py-4 border-b border-navy/10 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="label-caps" style={{ color: GOLD }}>Kundenansicht · Live</div>
            <div className="font-display text-lg text-navy leading-tight mt-0.5 truncate">
              {property.name}
            </div>
            <div className="text-[11px] text-navy-dark/60 italic mt-0.5">
              So sieht der Kunde {property.name} gerade auf seinem Home-Screen.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid={`customer-preview-close-${property.id}`}
            aria-label="Schließen"
            className="shrink-0 w-8 h-8 flex items-center justify-center border border-navy/15 hover:border-navy text-navy-dark transition-colors bg-white"
          >
            <X className="w-4 h-4" strokeWidth={1.6} />
          </button>
        </div>

        <div className="px-5 pt-4">
          <div className="inline-flex border border-navy/15 bg-white" role="tablist" aria-label="Ansichts-Modus">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "saved"}
              onClick={() => setMode("saved")}
              data-testid={`customer-preview-mode-saved-${property.id}`}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-widest transition-colors ${
                mode === "saved" ? "bg-navy text-ivory" : "text-navy hover:bg-navy/5"
              }`}
            >
              Gespeicherter Zustand
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "staged"}
              disabled={!dirty}
              onClick={() => dirty && setMode("staged")}
              data-testid={`customer-preview-mode-staged-${property.id}`}
              title={dirty ? "Vorschau mit den offenen Änderungen" : "Keine ungespeicherten Änderungen"}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-widest transition-colors ${
                mode === "staged" && dirty
                  ? "bg-navy text-ivory"
                  : dirty
                    ? "text-navy hover:bg-navy/5"
                    : "text-navy-dark/30 cursor-not-allowed"
              }`}
            >
              Mit ungespeicherten Änderungen
            </button>
          </div>
        </div>

        <div className="p-5 pt-3">
          {mode === "staged" && dirty && (
            <div
              className="mb-4 p-3 flex items-start gap-2 text-[12px] leading-snug"
              style={{ background: withAlpha(STATUS_COLORS.blue, 0.08), borderLeft: `3px solid ${BLUE}`, color: NAVY }}
              data-testid={`customer-preview-staged-hint-${property.id}`}
            >
              <Eye className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.7} style={{ color: BLUE }} />
              <span>
                <strong className="font-medium">Vorschau mit offenen Änderungen.</strong> Der Kunde sieht diesen Zustand erst nach „Status speichern".
              </span>
            </div>
          )}
          {mode === "saved" && dirty && (
            <div
              className="mb-4 p-3 flex items-start gap-2 text-[12px] leading-snug"
              style={{ background: withAlpha(BRAND_COLORS.gold, 0.10), borderLeft: `3px solid ${GOLD}`, color: NAVY }}
              data-testid={`customer-preview-dirty-hint-${property.id}`}
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.7} style={{ color: GOLD_DARK }} />
              <span>
                <strong className="font-medium">Nicht gespeicherte Änderungen</strong> sind für den Kunden noch NICHT sichtbar. Zum Vergleich auf „Mit ungespeicherten Änderungen" umschalten.
              </span>
            </div>
          )}
          <CustomerStatusPreview property={active} schema={schema} testidPrefix={`preview-${property.id}`} />
          {updated && mode === "saved" && (
            <div className="text-[11px] text-navy-dark/55 italic mt-3 px-1">
              Zuletzt beim Kunden aktualisiert: {updated}
            </div>
          )}
          {mode === "staged" && (
            <div className="text-[11px] italic mt-3 px-1" style={{ color: BLUE }}>
              · Staging-Vorschau — noch nicht beim Kunden angekommen
            </div>
          )}
          <div className="mt-4 text-[11px] text-navy-dark/55 leading-snug">
            Der Kunden-Home-Screen aktualisiert diese Kacheln automatisch alle
            10 Sekunden — jede Speicherung erscheint dort binnen einer Poll-Runde live.
          </div>

          {/* QR-Kunden-Ansicht */}
          <div className="mt-5 pt-4 border-t border-navy/10">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="label-caps" style={{ color: GOLD }}>QR · Kundenansicht auf Handy</div>
                <div className="text-[11.5px] text-navy-dark/70 mt-0.5 leading-snug">
                  Erzeugt einen 2-Minuten-QR-Code. Scan öffnet auf deinem Handy die echte Kunden-App.
                </div>
              </div>
              {!qr && (
                <button
                  type="button"
                  onClick={requestQr}
                  disabled={qrLoading}
                  data-testid={`customer-preview-qr-generate-${property.id}`}
                  className="inline-flex items-center gap-2 text-navy px-3 py-2 text-[10.5px] tracking-[0.16em] uppercase border border-navy/25 hover:border-gold hover:text-gold-dark disabled:opacity-40 bg-white transition-colors shrink-0"
                >
                  {qrLoading
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.6} />
                    : <QrCode className="w-3.5 h-3.5" strokeWidth={1.6} />}
                  QR erzeugen
                </button>
              )}
            </div>

            {qrError && (
              <div
                className="mt-3 p-3 text-[12px] leading-snug"
                style={{ background: withAlpha(STATUS_COLORS.red, 0.08), borderLeft: `3px solid ${RED}`, color: RED }}
                data-testid={`customer-preview-qr-error-${property.id}`}
              >
                {qrError}
              </div>
            )}

            {qr && qr.qr_png_base64 && (
              <div
                className="mt-3 bg-white p-4 flex items-center gap-4 border border-navy/10"
                data-testid={`customer-preview-qr-panel-${property.id}`}
              >
                <img
                  src={qr.qr_png_base64}
                  alt={`QR-Code · Kundenansicht ${property.name}`}
                  className="w-32 h-32 shrink-0"
                  data-testid={`customer-preview-qr-image-${property.id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-navy text-[15px] leading-tight">
                    Auf Handy scannen
                  </div>
                  <div className="text-[11.5px] text-navy-dark/60 mt-1 leading-snug">
                    Öffnet als Kunde <strong className="font-medium">{qr.customer_name}</strong>. Nur du siehst diesen Link, er läuft ab in:
                  </div>
                  <div
                    className="font-mono text-[13px] font-medium mt-2"
                    style={{ color: remaining > 20 ? NAVY : RED }}
                    data-testid={`customer-preview-qr-countdown-${property.id}`}
                  >
                    {remaining > 0
                      ? `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")} min`
                      : "abgelaufen"}
                  </div>
                  {remaining <= 0 && (
                    <button
                      type="button"
                      onClick={requestQr}
                      className="mt-2 text-[10.5px] tracking-widest uppercase text-gold-dark hover:text-navy underline"
                      data-testid={`customer-preview-qr-regenerate-${property.id}`}
                    >
                      Neuen QR erzeugen
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * TilePickerModal — Admin wählt genau 6 Schema-Keys, die dem Kunden als
 * Home-Screen-Kacheln angezeigt werden. Änderungen greifen binnen 10 s
 * (Poll-Intervall) beim Kunden. Reihenfolge der Kacheln folgt der
 * Schema-Reihenfolge (nicht der Klick-Reihenfolge).
 */
function TilePickerModal({ property, schema, onClose, onSaved, onSchemaRefresh }) {
  const initial = (property.dashboard_tile_keys && property.dashboard_tile_keys.length > 0)
    ? property.dashboard_tile_keys
    : schema.slice(0, 6).map((it) => it.key);
  const [selected, setSelected] = useState(new Set(initial));
  const [saving, setSaving] = useState(false);

  // Feb 2026 · „+ Neue Kachel erstellen"-Inline-Formular direkt im
  // Tile-Picker — spart dem Admin den Umweg über zwei separate Fenster.
  // Sichtbare State-Maschine:
  //   createOpen = false  → nur der „+ Neue Kachel erstellen"-Button
  //   createOpen = true   → das Inline-Formular (Label + Icon)
  const [createOpen, setCreateOpen] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newIcon, setNewIcon] = useState("Circle");
  const [creating, setCreating] = useState(false);
  const NewIcon = resolveIcon(newIcon);
  const suggestedKey = newLabel
    .trim().toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_]+/g, "_").replace(/_+/g, "_")
    .replace(/^_|_$/g, "").slice(0, 40);
  const keyCollides = schema.some((it) => it.key === suggestedKey);
  const canCreate = newLabel.trim().length >= 2 && suggestedKey.length >= 2 && !keyCollides && !creating;

  const createTile = async (e) => {
    e?.preventDefault();
    if (!canCreate) return;
    setCreating(true);
    try {
      // Bestehendes Schema unverändert + neue Zeile am Ende.
      const items = [
        ...schema.map((it) => ({
          key: it.key,
          label: it.label,
          icon: it.icon,
          input_type: it.input_type,
          locked: !!it.locked,
          category: it.category || "sonstiges",
          placeholder: it.placeholder || null,
          hint: it.hint || null,
        })),
        {
          key: suggestedKey,
          label: newLabel.trim(),
          icon: newIcon || "Circle",
          input_type: "text",
          locked: false,
          category: "sonstiges",
          placeholder: null,
          hint: null,
        },
      ];
      await api.put("/team/live-status-schema", { items });
      toast.success(`Kachel „${newLabel.trim()}" angelegt — jetzt unten in der Liste auswählbar.`);
      // Neu erzeugte Kachel sofort vorselektieren, wenn noch Platz ist.
      setSelected((cur) => {
        if (cur.size >= 6) return cur;
        const n = new Set(cur);
        n.add(suggestedKey);
        return n;
      });
      // Elterngraph benachrichtigen — refreshSchema() → useLiveStatusSchema-
      // Hook lädt neu → schema-Prop dieser Modal-Instanz aktualisiert →
      // die neue Zeile erscheint in der Auswahlliste unten.
      await onSchemaRefresh?.();
      setNewLabel("");
      setNewIcon("Circle");
      setCreateOpen(false);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally { setCreating(false); }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggle = (key) => {
    setSelected((cur) => {
      const n = new Set(cur);
      if (n.has(key)) n.delete(key);
      else if (n.size < 6) n.add(key);
      else return cur; // schon 6 gewählt — Klick ignoriert
      return n;
    });
  };

  const canSave = selected.size === 6;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      // Kacheln in Schema-Reihenfolge speichern.
      const orderedKeys = schema.map((it) => it.key).filter((k) => selected.has(k));
      const { data } = await api.put(`/team/properties/${property.id}/tile-keys`, {
        dashboard_tile_keys: orderedKeys,
      });
      toast.success(`Kachel-Auswahl gespeichert · ${property.name} sieht die neuen Kacheln in wenigen Sekunden.`);
      onSaved?.(data);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-0 md:p-6"
      style={{ background: withAlpha(BRAND_COLORS.navy, 0.55) }}
      onClick={onClose}
      data-testid={`tile-picker-modal-${property.id}`}
    >
      <div
        className="bg-ivory w-full md:max-w-[560px] shadow-2xl relative animate-fade-in"
        style={{ borderRadius: "12px 12px 0 0", maxHeight: "88vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-ivory/95 backdrop-blur px-5 py-4 border-b border-navy/10 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="label-caps" style={{ color: GOLD }}>Kachel-Auswahl</div>
            <div className="font-display text-lg text-navy leading-tight mt-0.5 truncate">
              {property.name}
            </div>
            <div className="text-[11.5px] text-navy-dark/60 italic mt-0.5">
              Wähle genau 6 Punkte, die dem Kunden als Kacheln angezeigt werden.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid={`tile-picker-close-${property.id}`}
            aria-label="Schließen"
            className="shrink-0 w-8 h-8 flex items-center justify-center border border-navy/15 hover:border-navy text-navy-dark transition-colors bg-white"
          >
            <X className="w-4 h-4" strokeWidth={1.6} />
          </button>
        </div>

        <div className="p-5">
          <div
            className="mb-4 p-3 flex items-start gap-2 text-[12px] leading-snug"
            style={{ background: selected.size === 6
              ? withAlpha(STATUS_COLORS.green, 0.10)
              : withAlpha(STATUS_COLORS.blue, 0.08),
              borderLeft: `3px solid ${selected.size === 6 ? GREEN : BLUE}`, color: NAVY }}
            data-testid={`tile-picker-count-${property.id}`}
          >
            <span>
              <strong className="font-medium">{selected.size} / 6</strong> Kacheln gewählt.
              {selected.size < 6 && ` Wähle noch ${6 - selected.size}.`}
              {selected.size === 6 && " Bereit zum Speichern."}
            </span>
          </div>

          <ul className="space-y-1.5">
            {schema.map((it) => {
              const Icon = resolveIcon(it.icon);
              const isChecked = selected.has(it.key);
              const canToggle = isChecked || selected.size < 6;
              return (
                <li key={it.key}>
                  <label
                    className={`flex items-center gap-3 p-3 bg-white border transition-colors ${
                      canToggle ? "cursor-pointer hover:border-gold" : "cursor-not-allowed opacity-40"
                    }`}
                    style={{ borderColor: isChecked ? GOLD : withAlpha(BRAND_COLORS.navy, 0.1) }}
                    data-testid={`tile-picker-row-${property.id}-${it.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={!canToggle}
                      onChange={() => toggle(it.key)}
                      data-testid={`tile-picker-checkbox-${property.id}-${it.key}`}
                      className="w-4 h-4 accent-[#0F1E3D]"
                    />
                    <span className="w-8 h-8 rounded flex items-center justify-center shrink-0" style={{ background: IVORY }}>
                      <Icon className="w-4 h-4" strokeWidth={1.5} style={{ color: NAVY }} />
                    </span>
                    <span className="flex-1 min-w-0 font-display text-[15px] text-navy">
                      {it.label}
                    </span>
                    {isChecked && (
                      <Check className="w-4 h-4 shrink-0" style={{ color: GOLD }} strokeWidth={2} />
                    )}
                  </label>
                </li>
              );
            })}
          </ul>

          {/* Feb 2026 · Inline „+ Neue Kachel erstellen" — spart Umweg über
              zwei Fenster. Button oder Formular je nach State. */}
          {!createOpen ? (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              data-testid={`tile-picker-create-open-${property.id}`}
              className="mt-3 w-full flex items-center justify-center gap-2 border border-dashed p-3 text-[12px] tracking-widest uppercase text-navy hover:text-gold-dark hover:border-gold transition-colors bg-white"
              style={{ borderColor: withAlpha(BRAND_COLORS.navy, 0.25) }}
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={2} />
              Neue Kachel erstellen
            </button>
          ) : (
            <form
              onSubmit={createTile}
              className="mt-3 border p-4 space-y-3 bg-white"
              style={{ borderColor: GOLD, background: withAlpha(BRAND_COLORS.gold, 0.05) }}
              data-testid={`tile-picker-create-form-${property.id}`}
            >
              <div className="flex items-start gap-2">
                <Plus className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={2} style={{ color: GOLD_DARK }} />
                <div className="text-[12px] text-navy-dark/70 leading-relaxed">
                  Neue Team-interne Kachel anlegen. Sie erscheint sofort in der Liste oben und kann direkt als eine der 6 Kacheln ausgewählt werden.
                </div>
              </div>
              <div>
                <label className="label-caps block mb-1" htmlFor={`tile-create-label-${property.id}`}>Name</label>
                <input
                  id={`tile-create-label-${property.id}`}
                  autoFocus
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="z. B. Pool-Status"
                  maxLength={80}
                  disabled={creating}
                  data-testid={`tile-picker-create-label-${property.id}`}
                  className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy"
                />
                <div className="text-[10px] text-navy-dark/50 mt-1">
                  Schlüssel: <code className="font-mono">{suggestedKey || "—"}</code>
                  {keyCollides && (
                    <span className="ml-2" style={{ color: RED }}>· bereits vorhanden</span>
                  )}
                </div>
              </div>
              <div>
                <label className="label-caps block mb-1" htmlFor={`tile-create-icon-${property.id}`}>Icon</label>
                <div className="flex items-center gap-2">
                  <span
                    className="w-9 h-9 rounded flex items-center justify-center shrink-0"
                    style={{ background: IVORY }}
                    data-testid={`tile-picker-create-icon-preview-${property.id}`}
                  >
                    <NewIcon className="w-4 h-4" strokeWidth={1.5} style={{ color: NAVY }} />
                  </span>
                  <select
                    id={`tile-create-icon-${property.id}`}
                    value={newIcon}
                    onChange={(e) => setNewIcon(e.target.value)}
                    disabled={creating}
                    data-testid={`tile-picker-create-icon-${property.id}`}
                    className="flex-1 bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy"
                  >
                    {ICON_CHOICES.map((ic) => (
                      <option key={ic.value} value={ic.value}>{ic.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => { setCreateOpen(false); setNewLabel(""); setNewIcon("Circle"); }}
                  disabled={creating}
                  data-testid={`tile-picker-create-cancel-${property.id}`}
                  className="text-[10.5px] tracking-widest uppercase px-3 py-2 text-navy-dark/60 hover:text-navy"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={!canCreate}
                  data-testid={`tile-picker-create-submit-${property.id}`}
                  className="inline-flex items-center gap-1.5 text-ivory px-4 py-2 text-[10.5px] tracking-[0.15em] uppercase hover:opacity-90 disabled:opacity-40"
                  style={{ background: NAVY }}
                >
                  {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" strokeWidth={2} />}
                  Kachel anlegen
                </button>
              </div>
            </form>
          )}

          <div className="mt-5 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="text-[11px] tracking-widest uppercase px-4 py-2.5 text-navy hover:text-gold-dark"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!canSave || saving}
              data-testid={`tile-picker-save-${property.id}`}
              className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-40"
              style={{ background: NAVY }}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" strokeWidth={1.6} />}
              Kachel-Auswahl speichern
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
